import React, { useEffect, useState } from "react";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import { type Lease } from "../../../store/api/leasesApi";

interface RenewLeaseModalProps {
	isOpen: boolean;
	onClose: () => void;
	lease: Lease | null;
	onRenew: (renewalData: {
		leaseId: string;
		duration: number; // months
		rentIncrease?: number; // percentage
		newRent?: number; // calculated new rent
		startDate: string;
		endDate: string;
	}) => void;
}

const RenewLeaseModal: React.FC<RenewLeaseModalProps> = ({
	isOpen,
	onClose,
	lease,
	onRenew,
}) => {
	const [duration, setDuration] = useState<number>(12);
	const [rentIncrease, setRentIncrease] = useState<string>("");
	const [newRent, setNewRent] = useState<number>(0);
	const [errors, setErrors] = useState<Record<string, string>>({});

	useEffect(() => {
		if (lease && isOpen) {
			// Calculate default duration based on current lease term
			const start = new Date(lease.start_date);
			const end = new Date(lease.end_date);
			const months = Math.ceil(
				(end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24 * 30),
			);
			setDuration(months || 12);
			setRentIncrease("");
			setErrors({});
		}
	}, [lease, isOpen]);

	useEffect(() => {
		if (lease && rentIncrease) {
			const increasePercent = parseFloat(rentIncrease) || 0;
			const currentRent = lease.monthly_rent || 0;
			const calculatedRent = currentRent * (1 + increasePercent / 100);
			setNewRent(Math.round(calculatedRent * 100) / 100);
		} else if (lease) {
			setNewRent(lease.monthly_rent || 0);
		}
	}, [rentIncrease, lease]);

	if (!isOpen || !lease) return null;

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		setErrors({});

		// Validation
		if (duration < 1 || duration > 120) {
			setErrors({ duration: "Duration must be between 1 and 120 months" });
			return;
		}

		// Calculate new dates
		const currentEndDate = new Date(lease.end_date);
		const startDate = new Date(currentEndDate);
		startDate.setDate(startDate.getDate() + 1); // Start day after current lease ends

		const endDate = new Date(startDate);
		endDate.setMonth(endDate.getMonth() + duration);

		onRenew({
			leaseId: lease.id,
			duration,
			rentIncrease: rentIncrease ? parseFloat(rentIncrease) : undefined,
			newRent,
			startDate: startDate.toISOString().split("T")[0],
			endDate: endDate.toISOString().split("T")[0],
		});
	};

	const currentRent = lease.monthly_rent || 0;

	return (
		<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1200 p-4">
			<div className="bg-card border border-border rounded-lg w-full max-w-md modal-shadow">
				{/* Header */}
				<div className="p-6 border-b border-border">
					<div className="flex items-center justify-between">
						<div>
							<h2 className="text-xl font-semibold text-foreground">
								Renew Lease
							</h2>
							<p className="text-sm text-muted-foreground mt-1">
								Lease Code: {lease.lease_code || "N/A"}
							</p>
						</div>
						<Button
							variant="ghost"
							size="icon"
							onClick={onClose}
							iconName="X"
						/>
					</div>
				</div>

				{/* Content */}
				<form onSubmit={handleSubmit}>
					<div className="p-6 space-y-4">
						{/* Current Lease Info */}
						<div className="bg-muted/30 rounded-lg p-4 space-y-2">
							<p className="text-sm font-medium text-foreground">
								Current Lease Information
							</p>
							<div className="grid grid-cols-2 gap-2 text-sm">
								<div>
									<span className="text-muted-foreground">Current Rent:</span>
									<p className="font-medium text-foreground">
										₦{currentRent.toLocaleString("en-NG")}
									</p>
								</div>
								<div>
									<span className="text-muted-foreground">End Date:</span>
									<p className="font-medium text-foreground">
										{new Date(lease.end_date).toLocaleDateString("en-NG")}
									</p>
								</div>
							</div>
						</div>

						{/* Duration */}
						<div>
							<label className="block text-sm font-medium text-foreground mb-2">
								New Lease Duration (months) *
							</label>
							<Input
								type="number"
								min="1"
								max="120"
								value={duration.toString()}
								onChange={(e) => setDuration(parseInt(e.target.value) || 12)}
								required
							/>
							{errors.duration && (
								<p className="mt-1 text-sm text-destructive">
									{errors.duration}
								</p>
							)}
						</div>

						{/* Rent Increase */}
						<div>
							<label className="block text-sm font-medium text-foreground mb-2">
								Rent Increase (%)
							</label>
							<Input
								type="number"
								step="0.1"
								min="0"
								placeholder="0"
								value={rentIncrease}
								onChange={(e) => setRentIncrease(e.target.value)}
							/>
							<p className="mt-1 text-xs text-muted-foreground">
								Leave empty to maintain current rent
							</p>
						</div>

						{/* New Rent Preview */}
						{rentIncrease && (
							<div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
								<div className="flex items-center justify-between">
									<span className="text-sm font-medium text-foreground">
										New Monthly Rent:
									</span>
									<span className="text-lg font-bold text-primary">
										₦
										{newRent.toLocaleString("en-NG", {
											minimumFractionDigits: 2,
										})}
									</span>
								</div>
								{rentIncrease && (
									<p className="text-xs text-muted-foreground mt-1">
										{parseFloat(rentIncrease) > 0 ? "+" : ""}
										{rentIncrease}% increase
									</p>
								)}
							</div>
						)}

						{/* New Dates Preview */}
						<div className="bg-muted/30 rounded-lg p-4 space-y-2">
							<p className="text-sm font-medium text-foreground">
								New Lease Period
							</p>
							<div className="grid grid-cols-2 gap-2 text-sm">
								<div>
									<span className="text-muted-foreground">Start Date:</span>
									<p className="font-medium text-foreground">
										{new Date(lease.end_date).toLocaleDateString("en-NG", {
											year: "numeric",
											month: "short",
											day: "numeric",
										})}
									</p>
								</div>
								<div>
									<span className="text-muted-foreground">End Date:</span>
									<p className="font-medium text-foreground">
										{(() => {
											const endDate = new Date(lease.end_date);
											endDate.setMonth(endDate.getMonth() + duration);
											return endDate.toLocaleDateString("en-NG", {
												year: "numeric",
												month: "short",
												day: "numeric",
											});
										})()}
									</p>
								</div>
							</div>
						</div>
					</div>

					{/* Footer */}
					<div className="p-6 border-t border-border">
						<div className="flex items-center justify-end space-x-3">
							<Button variant="ghost" type="button" onClick={onClose}>
								Cancel
							</Button>
							<Button type="submit" iconName="Check" iconPosition="left">
								Renew Lease
							</Button>
						</div>
					</div>
				</form>
			</div>
		</div>
	);
};

export default RenewLeaseModal;
